import type MockAdapter from 'axios-mock-adapter'
import authConfig from 'src/configs/auth'
import { LOCAL_DB_PERSONA_KEY } from '../config'
import { currentUserId } from '../engine/context'
import { findRow, readTable } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'

/**
 * Auth & identitas local mode: `/auth/login|refresh|logout`, `/users/me`, dan `/api/user/me`.
 * `/api/user/me` disusun dari tabel registry seperti backend asli:
 * mt_privileges → mt_roles → mt_capabilities → mt_pages → mt_clusters → mt_modules → mt_products.
 */

const TEN_YEARS_SECONDS = 10 * 365 * 24 * 60 * 60

const base64Url = (value: unknown) =>
  btoa(JSON.stringify(value)).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_')

/** JWT tak bertanda tangan; interceptor `client.api` hanya men-decode payload untuk cek `exp`. */
export function createLocalToken(userId: string): string {
  const now = Math.floor(Date.now() / 1000)
  const payload = {
    id: userId,
    role: 'local',
    app_access: true,
    admin_access: false,
    iat: now,
    exp: now + TEN_YEARS_SECONDS
  }
  return `${base64Url({ alg: 'none', typ: 'JWT' })}.${base64Url(payload)}.local`
}

/** Login sebagai persona: set token + persona aktif di localStorage. */
export function signInPersona(userId: string): void {
  localStorage.setItem(LOCAL_DB_PERSONA_KEY, userId)
  localStorage.setItem(authConfig.accessTokenKeyName, createLocalToken(userId))
  localStorage.setItem(authConfig.refreshTokenKeyName, `local-refresh-${userId}`)
}

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')

const byOrder = (a: LocalRow, b: LocalRow) => Number(a.order ?? 0) - Number(b.order ?? 0)

function rolesOfUser(userId: string): LocalRow[] {
  return readTable('mt_privileges')
    .filter(privilege => idOf(privilege.user) === userId && privilege.status !== 'archived')
    .map(privilege => findRow('mt_roles', idOf(privilege.role)))
    .filter((role): role is LocalRow => Boolean(role))
}

function productTree(role: LocalRow) {
  const capabilities = readTable('mt_capabilities').filter(cap => idOf(cap.role) === role.id)
  const pages = readTable('mt_pages').map(
    (page): LocalRow => ({
      ...page,
      capabilities: capabilities.filter(cap => idOf(cap.page) === page.id)
    })
  )
  const clusters = readTable('mt_clusters')
  const modules = readTable('mt_modules')
  const productPages = readTable('trx_product_pages')

  return readTable('mt_products')
    .filter(product =>
      productPages.some(
        link => idOf(link.product) === product.id && capabilities.some(cap => idOf(cap.page) === idOf(link.page))
      )
    )
    .sort(byOrder)
    .map(product => {
      const pageIds = new Set(
        productPages.filter(link => idOf(link.product) === product.id).map(link => idOf(link.page))
      )
      const productModules = modules
        .filter(mod => idOf(mod.product) === product.id)
        .sort(byOrder)
        .map(mod => ({
          ...mod,
          clusters: clusters
            .filter(cluster => idOf(cluster.module) === mod.id)
            .sort(byOrder)
            .map(cluster => ({
              ...cluster,
              pages: pages.filter(page => idOf(page.cluster) === cluster.id && pageIds.has(page.id)).sort(byOrder)
            }))
        }))
      return { ...product, modules: productModules, generalPages: [] }
    })
}

export function buildUserMe(userId: string) {
  const user = findRow('directus_users', userId)
  if (!user) return null
  return {
    ...user,
    roles: rolesOfUser(userId).map(role => ({ ...role, products: productTree(role) }))
  }
}

export function registerAuthHandlers(mock: MockAdapter) {
  mock.onPost('/auth/login').reply(config => {
    const body = JSON.parse(String(config.data ?? '{}')) as { email?: string }
    const user = readTable('directus_users').find(row => row.email === body.email) ?? readTable('directus_users')[0]
    if (!user) return [401, { errors: [{ message: 'Persona local mode belum di-seed.' }] }]
    signInPersona(user.id)
    return [
      200,
      {
        data: {
          access_token: createLocalToken(user.id),
          refresh_token: `local-refresh-${user.id}`,
          expires: TEN_YEARS_SECONDS * 1000
        }
      }
    ]
  })

  mock.onPost('/auth/refresh').reply(() => {
    const userId = currentUserId()
    return [
      200,
      {
        data: {
          access_token: createLocalToken(userId),
          refresh_token: `local-refresh-${userId}`,
          expires: TEN_YEARS_SECONDS * 1000
        }
      }
    ]
  })

  mock.onPost('/auth/logout').reply(() => [204])

  /** Dipakai form login: cek `is_using_generated_password` berdasarkan e-mail. */
  mock.onGet('/users').reply(config => {
    const raw = config.params?.filter
    const filter = typeof raw === 'string' ? JSON.parse(raw) : raw
    const email = filter?.email?._eq
    const users = readTable('directus_users').filter(row => !email || row.email === email)
    return [200, { data: users.map(row => ({ id: row.id, is_using_generated_password: false })) }]
  })

  /** Konfigurasi global login: local mode selalu memakai metode password biasa (tanpa OTP). */
  mock.onGet('/items/global_config').reply(() => [200, { data: { sign_in_method: 'PASSWORD' } }])

  mock.onGet('/users/me').reply(() => {
    const user = findRow('directus_users', currentUserId())
    return user ? [200, { data: user }] : [401, { errors: [{ message: 'Belum login.' }] }]
  })

  mock.onGet('/api/user/me').reply(() => {
    const me = buildUserMe(currentUserId())
    return me ? [200, { data: me }] : [401, { message: 'Belum login.' }]
  })
}
